/**
 * @fileoverview FlipWords component
 * @description Renders animated text that cycles through an array of words with smooth transitions.
 * Optimized for SSR/SSG with proper hydration handling and SEO-friendly fallbacks.
 * @module ui/about/FlipWords
 */

"use client"

import { 
  useCallback, 
  useEffect, 
  useState, 
  memo, 
  useMemo, 
  useRef,
  type FC,
  type ComponentPropsWithoutRef 
} from "react"
import { AnimatePresence, motion, type Variants } from "motion/react"
import { cn } from "~/lib/utils"

/**
 * Animation variants for the flip word transitions
 * Optimized for performance with hardware acceleration hints
 * @type {Variants}
 */
const flipWordVariants: Variants = {
  initial: { 
    opacity: 0, 
    y: 20,
    filter: "blur(4px)",
    scale: 0.95
  },
  animate: { 
    opacity: 1, 
    y: 0,
    filter: "blur(0px)",
    scale: 1,
    transition: { 
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94], // Custom easing for smoother animation
      staggerChildren: 0.1
    }
  },
  exit: { 
    opacity: 0, 
    y: -20, 
    filter: "blur(8px)",
    scale: 0.95,
    transition: { 
      duration: 0.3,
      ease: [0.55, 0.06, 0.68, 0.19]
    }
  }
} as const

/**
 * Props for the FlipWords component
 * @interface FlipWordsProps
 * @extends {Omit<ComponentPropsWithoutRef<'div'>, 'children' | 'onAnimationStart'>}
 * @property {string[]} words - Array of words to cycle through
 * @property {number} duration=3000 - Duration in milliseconds between word transitions
 * @property {boolean} autoPlay=true - Whether to automatically cycle through words
 * @property {(word: string, index: number) => void} onWordChange - Callback fired when the word changes
 * @property {() => void} onAnimationStart - Callback fired when animation starts
 * @property {() => void} onAnimationComplete - Callback fired when animation completes
 * @property {boolean} showAllForSEO=true - Whether to show all words for SEO (server-side only)
 * @property {string} seoSeparator=', ' - Separator for SEO text when showing all words
 * @property {string} aria-label - ARIA label for accessibility
 */
interface FlipWordsProps extends Omit<ComponentPropsWithoutRef<'div'>, 'children' | 'onAnimationStart'> {
  /** Array of words to cycle through */
  words: string[]
  /** Duration in milliseconds between word transitions */
  duration?: number
  /** Whether to automatically cycle through words */
  autoPlay?: boolean
  /** Callback fired when the word changes */
  onWordChange?: (word: string, index: number) => void
  /** Callback fired when animation starts */
  onAnimationStart?: () => void
  /** Callback fired when animation completes */
  onAnimationComplete?: () => void
  /** Whether to show all words for SEO (server-side only) */
  showAllForSEO?: boolean
  /** Separator for SEO text when showing all words */
  seoSeparator?: string
  /** ARIA label for accessibility */
  'aria-label'?: string
}

/**
 * Default configuration for the FlipWords component
 * @type {Readonly<{duration: number, autoPlay: boolean, showAllForSEO: boolean, seoSeparator: string}>}
 */
const DEFAULT_CONFIG = {
  duration: 3000,
  autoPlay: true,
  showAllForSEO: true,
  seoSeparator: ', ',
} as const

/**
 * Custom hook for managing hydration state
 * Prevents hydration mismatches in SSR environments
 * @returns {boolean} Whether the component has been hydrated on the client
 */
const useIsHydrated = (): boolean => {
  const [isHydrated, setIsHydrated] = useState<boolean>(false)
  
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot SSR hydration flag, set once after mount (client-only)
    setIsHydrated(true)
  }, [])
  
  return isHydrated
}

/**
 * Return type for the useFlipWords hook
 * @interface UseFlipWordsReturn
 * @property {string} currentWord - Current word being displayed
 * @property {number} currentIndex - Current index in the words array
 * @property {boolean} isAnimating - Whether an animation is currently in progress
 * @property {boolean} isSingleWord - Whether there's only one word (no animation needed)
 * @property {boolean} isHydrated - Whether the component has been hydrated
 * @property {() => void} onExitComplete - Callback for when exit animation completes
 * @property {() => void} startAnimation - Function to manually start animation
 */
interface UseFlipWordsReturn {
  /** Current word being displayed */
  currentWord: string
  /** Current index in the words array */
  currentIndex: number
  /** Whether an animation is currently in progress */
  isAnimating: boolean
  /** Whether there's only one word (no animation needed) */
  isSingleWord: boolean
  /** Whether the component has been hydrated */
  isHydrated: boolean
  /** Callback for when exit animation completes */
  onExitComplete: () => void
  /** Function to manually start animation */
  startAnimation: () => void
}

/**
 * Custom hook for managing flip word state and logic
 * Encapsulates all stateful logic for better testability
 * @param {string[]} words - Array of words to cycle through
 * @param {number} duration - Duration between transitions in milliseconds
 * @param {boolean} autoPlay - Whether to automatically cycle
 * @param {(word: string, index: number) => void} [onWordChange] - Word change callback
 * @param {() => void} [onAnimationStart] - Animation start callback
 * @param {() => void} [onAnimationComplete] - Animation complete callback
 * @returns {UseFlipWordsReturn} Hook state and methods
 */
const useFlipWords = (
  words: string[],
  duration: number,
  autoPlay: boolean,
  onWordChange?: (word: string, index: number) => void,
  onAnimationStart?: () => void,
  onAnimationComplete?: () => void
): UseFlipWordsReturn => {
  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const [isAnimating, setIsAnimating] = useState<boolean>(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isHydrated = useIsHydrated()

  // Compute currentWord / isSingleWord BEFORE any effects/refs that use them
  const currentWord = useMemo(() => (words.length ? words[currentIndex] ?? "" : ""), [words, currentIndex])
  const isSingleWord = useMemo(() => words.length <= 1, [words])

  // Refs for stable callback handling (initialized with current values)
  const hasFiredInitialRef = useRef(false)
  const onWordChangeRef = useRef(onWordChange)
  useEffect(() => { onWordChangeRef.current = onWordChange }, [onWordChange])

  const currentWordRef = useRef(currentWord)
  useEffect(() => { currentWordRef.current = currentWord }, [currentWord])

  const currentIndexRef = useRef(currentIndex)
  useEffect(() => { currentIndexRef.current = currentIndex }, [currentIndex])

  /**
   * Advances to the next word in the array
   * @function startAnimation
   * @returns {void}
   */
  const startAnimation = useCallback((): void => {
    if (isSingleWord || !isHydrated) return
    
    onAnimationStart?.()
    setIsAnimating(true)
    
    const nextIndex = (currentIndex + 1) % words.length
    setCurrentIndex(nextIndex)
    
    // Call the callback if provided
    const nextWord = words[nextIndex]
    if (nextWord && onWordChange) {
      onWordChange(nextWord, nextIndex)
    }
  }, [words, currentIndex, onWordChange, isSingleWord, isHydrated, onAnimationStart])

  /**
   * Handles the completion of exit animation
   * @function onExitComplete
   * @returns {void}
   */
  const onExitComplete = useCallback((): void => {
    setIsAnimating(false)
    onAnimationComplete?.()
  }, [onAnimationComplete])

  /**
   * Effect to manage the automatic word cycling
   */
  useEffect(() => {
    if (!autoPlay || isSingleWord || isAnimating || !isHydrated) return

    timerRef.current = setTimeout(startAnimation, duration)
    
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [isAnimating, duration, startAnimation, autoPlay, isSingleWord, isHydrated])

  /**
   * Effect to handle initial word change callback after hydration
   * Fires exactly once when hydration becomes true
   */
  useEffect(() => {
    if (!isHydrated || hasFiredInitialRef.current) return
    hasFiredInitialRef.current = true
    onWordChangeRef.current?.(currentWordRef.current, currentIndexRef.current)
  }, [isHydrated]) // ✅ linter satisfied, fires once after hydration

  /**
   * Cleanup effect
   */
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  return {
    currentWord,
    currentIndex,
    isAnimating,
    isSingleWord,
    isHydrated,
    onExitComplete,
    startAnimation
  }
}

/**
 * A React component that animates through an array of words with smooth flip transitions.
 * Uses Motion for fluid animations and supports customizable timing and styling.
 * 
 * Features:
 * - SSR/SSG compatible with proper hydration handling
 * - SEO-friendly fallbacks for server-side rendering
 * - Accessible with proper ARIA labels
 * - Performance optimized with hardware acceleration
 * - Fully typed with TypeScript
 * - Composable with standard div props
 * 
 * @example
 * ```tsx
 * <FlipWords
 *   words={["Hello", "World", "React"]}
 *   duration={2000}
 *   className="text-lg font-bold"
 *   onWordChange={(word, index) => console.log(`Current word: ${word}`)}
 *   aria-label="Animated text cycling through greetings"
 * />
 * ```
 * 
 * @param {FlipWordsProps} props - Component props
 * @returns {JSX.Element | null} The rendered component or null if invalid props
 */
export const FlipWords: FC<FlipWordsProps> = memo(({
  words,
  duration = DEFAULT_CONFIG.duration,
  className,
  autoPlay = DEFAULT_CONFIG.autoPlay,
  onWordChange,
  onAnimationStart,
  onAnimationComplete,
  showAllForSEO = DEFAULT_CONFIG.showAllForSEO,
  seoSeparator = DEFAULT_CONFIG.seoSeparator,
  'aria-label': ariaLabel,
  ...divProps
}) => {
  const {
    currentWord,
    currentIndex,
    isAnimating,
    isSingleWord,
    isHydrated,
    onExitComplete,
  } = useFlipWords(
    words,
    duration,
    autoPlay,
    onWordChange,
    onAnimationStart,
    onAnimationComplete
  )

  /**
   * Memoized longest word for stable layout dimensions
   */
  const longestWord = useMemo(
    () => words.reduce((a, w) => (w.length > a.length ? w : a), ""),
    [words]
  )

  // Input validation
  if (!Array.isArray(words) || words.length === 0) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('FlipWords: words prop must be a non-empty array')
    }
    return null
  }

  // SEO fallback for server-side rendering
  if (!isHydrated && showAllForSEO) {
    return (
      <div
        {...divProps}
        className={cn(
          "inline-block text-center text-white/90 px-2 pb-1",
          className
        )}
        aria-label={ariaLabel ?? `Text cycling through: ${words.join(seoSeparator)}`}
      >
        {words.join(seoSeparator)}
      </div>
    )
  }

  // Static display for single word or non-hydrated state
  if (isSingleWord || !isHydrated) {
    return (
      <div
        {...divProps}
        className={cn(
          "inline-block text-center text-white/90 px-2 pb-1",
          className
        )}
        aria-label={ariaLabel ?? `Static text: ${currentWord}`}
      >
        {currentWord}
      </div>
    )
  }

  return (
    <div
      {...divProps}
      className={cn(
        "relative inline-block overflow-hidden pb-1",
        className
      )}
      aria-label={ariaLabel ?? `Animated text, currently showing: ${currentWord}`}
      aria-live="polite"
      aria-busy={isAnimating}
      role="status"
    >
      <AnimatePresence mode="wait" onExitComplete={onExitComplete}>
        <motion.span
          key={`${currentIndex}-${currentWord}`} // More specific key for better tracking
          variants={flipWordVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          className={cn(
            "absolute inset-0 flex items-center justify-center",
            "text-center text-white/90 px-2",
            "will-change-transform transform-gpu", // Hardware acceleration
          )}
          // Accessibility attributes
          aria-atomic="true"
          aria-relevant="text"
        >
          {currentWord}
        </motion.span>
      </AnimatePresence>
      
      {/* 
        Keep layout stable with longest word; memoize so it doesn't recompute every render.
      */}
      <span 
        className="invisible text-center text-white/90 px-2"
        aria-hidden="true"
      >
        {longestWord}
      </span>
    </div>
  )
})

FlipWords.displayName = 'FlipWords'

export default FlipWords

/**
 * Type exports for external use
 */
export type { FlipWordsProps, UseFlipWordsReturn }
